import { create } from 'zustand';
import type { Me, SettingsInput, Theme } from '@tg/shared';
import { DEFAULT_LOCALE, readStoredLocale, storeLocale, type Locale } from '@/i18n/locales';

/**
 * Global session state (HANDOFF "State management → Global").
 * - `balance` is the server truth; `displayBalance` is what the header odometer shows
 *   (it may lag while a game holds the credit back until its animation ends — see src/wallet).
 * - Preferences (theme, sound, music, reducedMotion) live here for guests too (localStorage),
 *   and are synced to the profile via PATCH /api/me/settings when signed in.
 * - Sound and music are OFF until the player turns them on (honest-play rule 4).
 */
export type SessionStatus = 'loading' | 'guest' | 'user';
export type ConnectionState = 'connecting' | 'online' | 'offline';

export interface Prefs {
  theme: Theme;
  sound: boolean;
  music: boolean;
  /** user toggle; effective reduced motion = this OR the OS setting */
  reducedMotion: boolean;
}

export interface SessionState extends Prefs {
  status: SessionStatus;
  me: Me | null;
  balance: number;
  displayBalance: number;
  locale: Locale;
  osReducedMotion: boolean;
  /** true when /api answered at least once (false → guest + mock lobby) */
  apiAvailable: boolean;
  connection: ConnectionState;
  /** socket was connected at least once → show "No connection" banner on loss */
  everConnected: boolean;
  dailyOpen: boolean;

  setMe(me: Me | null): void;
  setStatus(s: SessionStatus): void;
  setBalance(balance: number): void;
  setDisplayBalance(balance: number): void;
  setLocale(l: Locale): void;
  setPrefs(p: Partial<Prefs>): void;
  setOsReducedMotion(v: boolean): void;
  setApiAvailable(v: boolean): void;
  setConnection(c: ConnectionState): void;
  openDaily(open: boolean): void;
}

const PREFS_KEY = 'tg.prefs';
const THEME_KEY = 'tg.theme';

export const DEFAULT_PREFS: Prefs = { theme: 'dark', sound: false, music: false, reducedMotion: false };

function loadPrefs(): Prefs {
  try {
    const raw = localStorage.getItem(PREFS_KEY);
    if (!raw) return { ...DEFAULT_PREFS };
    const p = JSON.parse(raw) as Partial<Prefs>;
    return {
      theme: p.theme === 'light' || p.theme === 'system' || p.theme === 'dark' ? p.theme : 'dark',
      sound: p.sound === true,
      music: p.music === true,
      reducedMotion: p.reducedMotion === true,
    };
  } catch {
    return { ...DEFAULT_PREFS };
  }
}

export function savePrefs(p: Prefs): void {
  try {
    localStorage.setItem(PREFS_KEY, JSON.stringify(p));
    // read by the inline script in index.html before first paint
    localStorage.setItem(THEME_KEY, p.theme);
  } catch {
    /* ignore */
  }
}

function osReduced(): boolean {
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}

export const useSession = create<SessionState>()((set, get) => ({
  status: 'loading',
  me: null,
  balance: 0,
  displayBalance: 0,
  locale: readStoredLocale() ?? DEFAULT_LOCALE,
  ...loadPrefs(),
  osReducedMotion: osReduced(),
  apiAvailable: false,
  connection: 'connecting',
  everConnected: false,
  dailyOpen: false,

  setMe(me) {
    if (!me) {
      set({ me: null, status: 'guest', balance: 0, displayBalance: 0 });
      return;
    }
    const prefs: Prefs = { theme: me.theme, sound: me.sound, music: me.music, reducedMotion: me.reducedMotion };
    savePrefs(prefs);
    set({ me, status: 'user', balance: me.balance, displayBalance: me.balance, ...prefs });
  },
  setStatus: (status) => set({ status }),
  setBalance(balance) {
    const me = get().me;
    set({ balance, me: me ? { ...me, balance } : me });
  },
  setDisplayBalance: (displayBalance) => set({ displayBalance }),
  setLocale(locale) {
    storeLocale(locale);
    set({ locale });
  },
  setPrefs(p) {
    const next: Prefs = {
      theme: p.theme ?? get().theme,
      sound: p.sound ?? get().sound,
      music: p.music ?? get().music,
      reducedMotion: p.reducedMotion ?? get().reducedMotion,
    };
    savePrefs(next);
    set(next);
  },
  setOsReducedMotion: (osReducedMotion) => set({ osReducedMotion }),
  setApiAvailable: (apiAvailable) => set({ apiAvailable }),
  setConnection: (connection) =>
    set((s) => ({ connection, everConnected: s.everConnected || connection === 'online' })),
  openDaily: (dailyOpen) => set({ dailyOpen }),
}));

/** Effective reduced motion: OS setting OR profile toggle. */
export const selectReducedMotion = (s: SessionState): boolean => s.reducedMotion || s.osReducedMotion;
export const selectIsUser = (s: SessionState): boolean => s.status === 'user';

/** Fields of SettingsInput that are preferences (for PATCH /api/me/settings). */
export type PrefPatch = Pick<SettingsInput, 'theme' | 'sound' | 'music' | 'reducedMotion' | 'locale'>;
